import { Injectable } from '@angular/core';
import { LoaderService } from './loader.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, delay, finalize } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TOKEN_NOT_VALID } from '../common.constants';

export interface ApiBaseOptions {
  url: string;
  queryParams?: Record<string, string>;
  headers?: HttpHeaders;
  withSpinner?: boolean;
}

export interface ApiPostOptions extends ApiBaseOptions {
  body: any;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {

  constructor(
    private loaderService: LoaderService,
    private http: HttpClient,
    private toastr: ToastrService,
  ) {
  }

  getOptions(options: ApiBaseOptions): { params: any; headers: HttpHeaders } {
    const queryParams = options.queryParams || {};
    return {headers: options.headers, params: queryParams};
  }

  get(options: ApiBaseOptions): Observable<any> {
    if (options.withSpinner) {
      this.loaderService.show('api get');
    }
    const httpOptions = this.getOptions(options);
    return this.http.get(options.url, httpOptions).pipe(
      finalize(() => {
        if (options.withSpinner) {
          this.loaderService.hide('api get');
        }
      }),
      catchError(error => this.handleError(error)),
    );
  }

  handleError(err) {
    console.log('error!!', err);
    if (err.status === 400) {
      // validation errors
      Object.values(err.error).forEach((msgs: string[]) => {
        if (msgs.length) {
          msgs.forEach(msg => {
            this.toastr.error(msg);
          });
        }
      });
    } else if (err.status === 401 && err.error.code === TOKEN_NOT_VALID) {
      const msg = err.error.messages[0].message;
      this.toastr.error(msg);
    } else {
      this.toastr.error('An error occurred on server side');
    }
    return throwError(err);
  }

  post(options: ApiPostOptions): Observable<any> {
    if (options.withSpinner) {
      this.loaderService.show('api post');
    }
    return this.http.post(options.url, options.body).pipe(
      finalize(() => {
        if (options.withSpinner) {
          this.loaderService.hide('api post');
        }
      }),
      catchError(error => this.handleError(error)),
    );
  }

  patch(options: ApiPostOptions): Observable<any> {
    if (options.withSpinner) {
      this.loaderService.show('api patch');
    }
    return this.http.patch(options.url, options.body).pipe(
      finalize(() => {
        if (options.withSpinner) {
          this.loaderService.hide('api path');
        }
      }),
      catchError(error => this.handleError(error)),
    );
  }

  delete(options: ApiBaseOptions): Observable<any> {
    if (options.withSpinner) {
      this.loaderService.show();
    }
    return this.http.delete(options.url).pipe(
      finalize(() => {
        if (options.withSpinner) {
          this.loaderService.hide();
        }
      }),
      catchError(error => this.handleError(error)),
    );
  }

  getFake(options: ApiBaseOptions, resp) {
    if (options.withSpinner) {
      this.loaderService.show();
    }
    const httpOptions = this.getOptions(options);
    return of(resp).pipe(
      delay(800),
      finalize(() => {
        if (options.withSpinner) {
          this.loaderService.hide();
        }
        console.log('emulated GET options - ', options);
      }),
    );
  }

  postFake(options: ApiPostOptions, resp: any = []) {
    if (options.withSpinner) {
      this.loaderService.show();
    }
    return of(resp).pipe(
      delay(800),
      finalize(() => {
        if (options.withSpinner) {
          this.loaderService.hide();
        }
        console.log('emulated POST options - ', options);
      }),
    );
  }
}
